import {api} from './api';
import { privateLambdaService } from './privateLambdaService';
import { Requirement, RequirementsTextResponse, RequirementUploadResponse, ImageInput } from '../types';
import authService from './authService';

/**
 * Service for managing requirement-related API operations
 */
export const requirementService = {
  /**
   * Get all requirements for a specific project
   * @param {string} projectId - Project ID
   * @returns {Promise<Requirement[]>} List of requirements
   */
  getRequirements: async (projectId: string): Promise<Requirement[]> => {
    try {
      return await privateLambdaService.getRequirements(projectId);
    } catch (error) {
      console.error('Error getting requirements:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await api.get(`/projects/${projectId}/requirements`);
      // return response.data;
    }
  },

  /**
   * Get a specific requirement by ID
   * @param {string} projectId - Project ID
   * @param {string} requirementId - Requirement ID
   * @returns {Promise<Requirement>} Requirement details
   */
  getRequirementById: async (projectId: string, requirementId: string): Promise<Requirement> => {
    try {
      return await privateLambdaService.getRequirementById(projectId, requirementId);
    } catch (error) {
      console.error('Error getting requirement by ID:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await api.get(`/projects/${projectId}/requirements/${requirementId}`);
      // return response.data;
    }
  },

  /**
   * Upload a requirement document
   * @param {string} projectId - Project ID
   * @param {File} file - The file to upload
   * @param {ImageInput[]} images - Optional images to upload
   * @returns {Promise<RequirementUploadResponse>} Upload response with tracking info
   */
  uploadRequirement: async (projectId: string, requirementType: string, file: File, images?: ImageInput[], replaceExisting?: boolean): Promise<RequirementUploadResponse> => {
    try {
      // Try private Lambda service first for presigned URL
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      // Get auth token and create headers
      const token = authService.getToken();
      const authHeaders = token ? { 'X-Auth-Token': `Bearer ${token}` } : undefined;
      
      const { data } = await privateLambdaService.callPrivateLambdaApi(
        '/api/requirements/uploadPresigned',
        'POST',
        {
          projectId,
          requirementFiles: [{
            fileExtension,
            fileType: requirementType,
          }],
          images: images, // Pass images to backend
          replaceExisting: replaceExisting || false, // When true, replaces old files and clears all artifacts
        },
        authHeaders
      );
      const presignedUrlInfo = data.presignedUrls[0];
      const uploadUrl = presignedUrlInfo.signedUrl || presignedUrlInfo.signedURL || presignedUrlInfo.url;
      const s3Key = presignedUrlInfo.fileKey;
      console.log('Uploading to S3 with key:', file.type, s3Key);
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      // Check for audio/video file extensions
      const audioVideoExtensions = ['mp3', 'wav', 'mp4', 'avi', 'm4a', 'aac', 'flac', 'ogg', 'mov', 'wmv'];
      let transcriptionResult = null;
      if (fileExtension && audioVideoExtensions.includes(fileExtension)) {
        // Call transcribe requirement API
        try {
          transcriptionResult = await privateLambdaService.transcribeRequirement(projectId);
        } catch (transcribeError) {
          console.error('Transcription failed:', transcribeError);
        }
      }

      return {
        status: 'success',
        message: 'File uploaded successfully',
        data: {
          projectId: presignedUrlInfo.projectId || projectId,
          requirementType,
          fileKey: s3Key,
          ...(transcriptionResult ? { transcriptionResult } : {}),
        },
      };
    } catch (error) {
      console.error('Error uploading requirement:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // return {
      //   status: 'success',
      //   message: 'File uploaded successfully',
      //   data: {
      //     projectId,
      //     requirementType,
      //     fileKey: "s3Key",
      //   },
      // };
    }
  },

  /**
   * Get the processing status of a requirement
   * @param {string} projectId - Project ID
   * @param {string} requirementId - Requirement ID
   * @returns {Promise<Requirement>} Updated requirement status
   */
  getRequirementStatus: async (projectId: string, requirementId: string): Promise<Requirement> => {
    try {
      return await privateLambdaService.getRequirementStatus(projectId, requirementId);
    } catch (error) {
      console.error('Error getting requirement status:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await api.get(`/projects/${projectId}/requirements/${requirementId}/status`);
      // return response.data;
    }
  },
  
  /**
   * Delete a requirement
   * @param {string} projectId - Project ID
   * @param {string} requirementId - Requirement ID
   * @returns {Promise<void>}
   */
  deleteRequirement: async (projectId: string, requirementId: string): Promise<void> => {
    try {
      await privateLambdaService.deleteRequirement(projectId, requirementId);
    } catch (error) {
      console.error('Error deleting requirement:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // await api.delete(`/projects/${projectId}/requirements/${requirementId}`);
    }
  },

  processRequirement: async (projectId: string) => {
    try {
      return await privateLambdaService.processRequirement(projectId);
    } catch (error) {
      console.error('Error processing requirement:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await api.post(`/requirements/${projectId}/process`);
      // return response.data;
    }
  },

  getRequirementsText: async (projectId: string): Promise<RequirementsTextResponse> => {
    try {
      return await privateLambdaService.getRequirementsText(projectId);
    } catch (error) {
      console.error('Error getting requirements text:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await api.get(`/requirements/${projectId}/text`);
      // return response.data;
    }
  },

  /**
   * Update requirements document for a specific project
   * @param {string} projectId - Project ID
   * @param {string} requirementsDoc - The requirements document as a string
   * @returns {Promise<any>} Response from the update API
   */
  updateRequirements: async (projectId: string, requirementsDoc: string): Promise<any> => {
    try {
      return await privateLambdaService.updateRequirements(projectId, requirementsDoc);
    } catch (error) {
      console.error('Error updating requirements:', error);
      throw error; // Re-throw to propagate backend error messages
      
      // FALLBACK TO PUBLIC API DISABLED - Backend error messages should be shown to users
      // const response = await api.post(`/requirements/${projectId}/update`, {
      //   requirementsDoc,
      // });
      // return response.data;
    }
  },

  /**
   * Approve a specific stage/artifact for a project
   * @param {string} projectId - Project ID
   * @param {string} stage - Stage to approve ('requirements' | 'jiraStories' | 'diagrams' | 'designDocument' | 'code')
   * @returns {Promise<any>} Approval response
   */
  approveRequirements: async (projectId: string, stage: string = 'requirements'): Promise<any> => {
    try {
      return await privateLambdaService.approveStage(projectId, stage);
    } catch (error) {
      console.error('Error approving stage:', error);
      throw error;
    }
  },
};

export default requirementService;